import React, { useState, useRef, useEffect, ReactNode } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import Spinner from "../Spinner";

export interface ImageProps {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  caption?: ReactNode;
  enableHoverZoom?: boolean;
  zoomScale?: number;
  zoomSide?: "left" | "right" | "auto";
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  showOnError?: boolean;
  className?: string;
  onClick?: () => void;
  loading?: "lazy" | "eager";
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
}

const ImageContainer = styled.div<{ hasCaption: boolean }>`
  display: inline-block;
  position: relative;
  ${({ hasCaption }) => hasCaption && `
    display: inline-flex;
    flex-direction: column;
    align-items: center;
  `}
`;

const ImageWrapper = styled.div<{
  width?: number | string;
  height?: number | string;
  enableHoverZoom?: boolean;
}>`
  position: relative;
  display: inline-block;
  width: ${({ width }) => typeof width === 'number' ? `${width}px` : width || 'auto'};
  height: ${({ height }) => typeof height === 'number' ? `${height}px` : height || 'auto'};
  overflow: ${({ enableHoverZoom }) => enableHoverZoom ? 'visible' : 'hidden'};

  ${({ enableHoverZoom }) => enableHoverZoom && `
    &:hover .zoom-preview {
      display: block;
    }
  `}
`;

const StyledImage = styled.img<{
  width?: number | string;
  height?: number | string;
  objectFit?: string;
  clickable?: boolean;
}>`
  width: ${({ width }) => typeof width === 'number' ? `${width}px` : width || 'auto'};
  height: ${({ height }) => typeof height === 'number' ? `${height}px` : height || 'auto'};
  object-fit: ${({ objectFit }) => objectFit || 'contain'};
  display: block;
  ${({ clickable }) => clickable && `cursor: pointer;`}
`;

const ZoomPreview = styled.div<{
  zoomScale: number;
  imageHeight?: number | string;
}>`
  position: fixed;
  z-index: 100000;
  background: white;
  border: 2px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  pointer-events: none;
  padding: 8px;

  img {
    display: block;
    width: auto;
    height: ${({ imageHeight, zoomScale }) => {
      if (typeof imageHeight === 'number') return `${imageHeight * zoomScale}px`;
      if (typeof imageHeight === 'string' && imageHeight.includes('px')) {
        const value = parseInt(imageHeight);
        return `${value * zoomScale}px`;
      }
      return '200px'; // default height
    }};
    max-height: 80vh;
    object-fit: contain;
  }
`;

const Caption = styled.div`
  margin-top: 8px;
  text-align: center;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.4;
`;

const LoadingContainer = styled.div<{
  width?: number | string;
  height?: number | string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => typeof width === 'number' ? `${width}px` : width || '100px'};
  height: ${({ height }) => typeof height === 'number' ? `${height}px` : height || '100px'};
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 4px;
`;

const Image: React.FC<ImageProps> = ({
  src,
  alt = "",
  width,
  height,
  caption,
  enableHoverZoom = false,
  zoomScale = 8,
  zoomSide = "auto",
  onError,
  showOnError = false,
  className,
  onClick,
  loading = "lazy",
  objectFit = "contain",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [zoomPosition, setZoomPosition] = useState<{
    x: number;
    y: number;
    anchor: "left" | "right";
  }>({ x: 0, y: 0, anchor: "left" });
  const [showZoom, setShowZoom] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reset error state when src changes
    setHasError(false);
    setIsLoading(false);
  }, [src]);

  const handleMouseEnter = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!enableHoverZoom) return;

    setShowZoom(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const gap = 10;

    // Calculate zoom height based on original height and scale
    const originalHeight = typeof height === 'number' ? height : 28;
    const zoomHeight = Math.min(originalHeight * zoomScale, 300) + 16; // max 300px + padding
    const estimatedZoomWidth = zoomHeight * 1.5; // Estimate width based on typical drug image aspect ratio

    let anchor: "left" | "right" = "left";
    let x: number;
    let y = rect.top;

    // zoomSide에 따른 위치 결정
    if (zoomSide === "left") {
      // 왼쪽에 고정 표시: CSS right 속성 사용
      anchor = "right";
      x = window.innerWidth - rect.left + gap;
    } else if (zoomSide === "right") {
      // 오른쪽에 고정 표시: CSS left 속성 사용
      anchor = "left";
      x = rect.right + gap;
    } else {
      // auto: 오른쪽 우선, 공간 부족 시 왼쪽
      const rightSpace = window.innerWidth - rect.right - gap;
      const leftSpace = rect.left - gap;

      if (rightSpace >= estimatedZoomWidth) {
        // 오른쪽에 공간 충분
        anchor = "left";
        x = rect.right + gap;
      } else if (leftSpace >= estimatedZoomWidth) {
        // 왼쪽에 공간 충분
        anchor = "right";
        x = window.innerWidth - rect.left + gap;
      } else {
        // 양쪽 다 부족: 왼쪽 정렬 (max-width로 제한됨)
        anchor = "left";
        x = gap;
      }
    }

    // Check if it goes beyond bottom edge
    if (y + zoomHeight > window.innerHeight - gap) {
      y = window.innerHeight - zoomHeight - gap;
    }

    // Check if it goes above top edge
    if (y < gap) {
      y = gap;
    }

    setZoomPosition({ x, y, anchor });
  };

  const handleMouseLeave = () => {
    setShowZoom(false);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false);
    setHasError(true);
    if (onError) {
      onError(e);
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  // Return null if error and showOnError is false
  if (hasError && !showOnError) {
    return null;
  }

  return (
    <ImageContainer hasCaption={!!caption} className={className}>
      <ImageWrapper
        width={width}
        height={height}
        enableHoverZoom={enableHoverZoom}
      >
        {isLoading && (
          <LoadingContainer width={width} height={height}>
            <Spinner />
          </LoadingContainer>
        )}

        <StyledImage
          ref={imageRef}
          src={src}
          alt={alt}
          width={width}
          height={height}
          objectFit={objectFit}
          onError={handleImageError}
          onLoad={handleImageLoad}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={onClick}
          clickable={!!onClick}
          loading={loading}
        />

      </ImageWrapper>

      {/* ZoomPreview를 Portal로 body에 직접 렌더링하여 overflow 문제 방지 */}
      {enableHoverZoom && showZoom && !isLoading && !hasError &&
        ReactDOM.createPortal(
          <ZoomPreview
            ref={zoomRef}
            className="zoom-preview"
            zoomScale={zoomScale}
            imageHeight={height}
            style={{
              ...(zoomPosition.anchor === "left"
                ? { left: `${zoomPosition.x}px` }
                : { right: `${zoomPosition.x}px` }),
              top: `${zoomPosition.y}px`,
              maxWidth: `calc(100vw - 20px)`,
            }}
          >
            <img src={src} alt={alt} />
          </ZoomPreview>,
          document.body
        )
      }

      {caption && (
        <Caption>{caption}</Caption>
      )}
    </ImageContainer>
  );
};

export default Image;